import React, { memo, useCallback, useRef, useEffect, useState } from 'react';
import { GoogleMap, useJsApiLoader, Polygon, Marker, Polyline, Circle } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['geometry'];

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  clickableIcons: false,
  gestureHandling: 'greedy',
  styles: [
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] }
  ]
};

/**
 * TrackPageGoogleMap - Google Maps component for delivery tracking
 * 
 * Features:
 * - Delivery boundary polygon
 * - Current position marker
 * - Route path polyline
 * - House markers (delivered and undelivered)
 * - Manual delivery markers
 * - Auto-follow mode when tracking
 */
const TrackPageGoogleMap = ({
  mapCenter = { lat: 29.7604, lng: -95.3698 },
  deliveryBoundary = null,
  zoneLabel = null,
  currentPosition = null,
  routePath = [],
  // Multi-contributor GPS trails: [{id, points: [[lat,lng],...], color}].
  // Used by the Admin Mark page to show where each worker walked.
  routeTrails = [],
  houseLocations = [],
  deliveredHouses = new Set(),
  deliveredHouseInfo = {},
  // Admin-flagged statuses: {house_id: 'non_deliverable' | 'gated' | 'safety'}.
  // These render as DARK RED dots and take precedence over delivered.
  statusHouseInfo = {},
  manualDeliveryLocations = [],
  nearbyHouse = null,
  isTracking = false,
  proximityRadius = 15,
  onMapClick = null,
  onMapReady = null,
  onHouseClick = null,
}) => {
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: 'google-maps-loader',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  const onLoad = useCallback((map) => {
    mapRef.current = map;
    setMapReady(true);
    // Expose the map handle to the parent so it can imperatively pan/zoom
    // (used by the Admin Mark page's address search).
    if (typeof onMapReady === 'function') {
      try { onMapReady(map); } catch (_) { /* ignore */ }
    }
  }, [onMapReady]);

  // Auto-follow current position when tracking
  useEffect(() => {
    if (mapRef.current && currentPosition && isTracking && mapReady) {
      mapRef.current.panTo({ lat: currentPosition[0], lng: currentPosition[1] });
      mapRef.current.setZoom(17);
    }
  }, [currentPosition, isTracking, mapReady]);

  // Fit bounds to delivery boundary when not tracking
  useEffect(() => {
    if (mapRef.current && deliveryBoundary && deliveryBoundary.length >= 3 && !isTracking && mapReady) {
      const bounds = new window.google.maps.LatLngBounds();
      deliveryBoundary.forEach(point => {
        bounds.extend({ lat: point[0], lng: point[1] });
      });
      mapRef.current.fitBounds(bounds, { padding: 30 });
    }
  }, [deliveryBoundary, isTracking, mapReady]);

  // Convert polygon from [[lat, lng], ...] to [{ lat, lng }, ...]
  const polygonPath = deliveryBoundary && deliveryBoundary.length >= 3
    ? deliveryBoundary.map(p => ({ lat: p[0], lng: p[1] }))
    : null;

  // Convert route path
  const routePolylinePath = routePath.length > 1
    ? routePath.map(p => Array.isArray(p) ? { lat: p[0], lng: p[1] } : p)
    : null;

  if (!isLoaded) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-900">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={{ height: '100%', width: '100%' }}
      center={mapCenter}
      zoom={15}
      options={mapOptions}
      onLoad={onLoad}
      onClick={onMapClick}
    >
      {/* Delivery Boundary Polygon */}
      {polygonPath && (
        <Polygon
          paths={polygonPath}
          options={{
            fillColor: '#f97316',
            fillOpacity: 0.15,
            strokeColor: '#f97316',
            strokeWeight: 3,
            strokeOpacity: 1
          }}
        />
      )}

      {/* Zone Label Marker */}
      {zoneLabel && mapCenter && (
        <Marker
          position={mapCenter}
          icon={{
            url: `data:image/svg+xml,${encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" width="80" height="30">
                <rect x="0" y="0" width="80" height="30" rx="8" fill="#f97316" opacity="0.95"/>
                <text x="40" y="20" font-family="Arial" font-size="14" font-weight="bold" fill="white" text-anchor="middle">${zoneLabel}</text>
              </svg>
            `)}`,
            scaledSize: { width: 80, height: 30 },
            anchor: { x: 40, y: 15 }
          }}
          zIndex={50}
        />
      )}

      {/* House location markers */}
      {houseLocations.map((house) => {
        const houseStatus = statusHouseInfo[house.id] || null; // 'non_deliverable' | 'gated' | 'safety'
        const isDelivered = !houseStatus && deliveredHouses.has(house.id);
        // Hover tooltip: attribution ("Delivered by X · time") when known,
        // plus the click hint on pages where dots are toggleable.
        const info = isDelivered ? deliveredHouseInfo[house.id] : null;
        let hoverTitle;
        if (onHouseClick) {
          hoverTitle = isDelivered ? 'Click to un-mark this delivery' : 'Click to mark as delivered';
        }
        if (houseStatus) {
          const label = { gated: 'Gated community', non_deliverable: 'Non deliverable', safety: 'Safety concern' }[houseStatus] || 'Flagged';
          hoverTitle = onHouseClick ? `${label}\nEnable the ${label} button and click to un-flag` : label;
        }
        if (info && info.user_name) {
          const when = info.at ? ` · ${new Date(info.at).toLocaleString()}` : '';
          const byAdmin = info.admin_name ? ` (admin-marked by ${info.admin_name})` : '';
          const attribution = `Delivered by ${info.user_name}${when}${byAdmin}`;
          hoverTitle = hoverTitle ? `${attribution}\n${hoverTitle}` : attribution;
        }
        // Dark red for flagged: X = non-deliverable, ! = safety, plain = gated.
        const fill = houseStatus ? '#7f1d1d' : (isDelivered ? '#fbbf24' : '#22c55e');
        const inner = houseStatus
          ? (houseStatus === 'non_deliverable'
            ? '<path d="M6.5 6.5 L13.5 13.5 M13.5 6.5 L6.5 13.5" stroke="white" stroke-width="2" stroke-linecap="round"/>'
            : (houseStatus === 'safety'
              ? '<rect x="8.9" y="4.5" width="2.2" height="7" rx="1.1" fill="white"/><circle cx="10" cy="14.5" r="1.4" fill="white"/>'
              : ''))
          : (isDelivered ? '<circle cx="10" cy="10" r="3" fill="#000"/>' : '');
        return (
          <Marker
            key={house.id}
            position={{ lat: house.lat, lng: house.lon }}
            icon={{
              url: `data:image/svg+xml,${encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="8" fill="${fill}" stroke="white" stroke-width="2"/>
                  ${inner}
                </svg>
              `)}`,
              scaledSize: { width: 20, height: 20 },
              anchor: { x: 10, y: 10 }
            }}
            zIndex={houseStatus ? 65 : (isDelivered ? 60 : 55)}
            // House dots become click-targets when `onHouseClick` is wired.
            // Used by the Admin Mark page to toggle delivered <-> undelivered
            // status by clicking the dot itself (cleaner than dropping a
            // separate purple admin marker on top of every house).
            clickable={!!onHouseClick}
            cursor={onHouseClick ? 'pointer' : undefined}
            onClick={onHouseClick ? (() => onHouseClick(house)) : undefined}
            title={hoverTitle}
          />
        );
      })}

      {/* Manual delivery markers - purple dots showing where deliveries were made */}
      {manualDeliveryLocations.map((delivery, index) => {
        // Check if this is a recently added marker (within last 3 seconds)
        const isRecent = delivery.timestamp && 
          (Date.now() - new Date(delivery.timestamp).getTime()) < 3000;
        
        const position = delivery.position 
          ? { lat: delivery.position[0], lng: delivery.position[1] }
          : delivery.lat && delivery.lng 
            ? { lat: delivery.lat, lng: delivery.lng }
            : null;
        
        if (!position) return null;

        // Anti-cheat: marks made far from any house door render red
        const markColor = delivery.flagged ? '#dc2626' : '#a855f7';

        return (
          <Marker
            key={delivery.id || `manual-${index}`}
            position={position}
            icon={{
              url: `data:image/svg+xml,${encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26">
                  ${isRecent ? `
                    <circle cx="13" cy="13" r="12" fill="none" stroke="${markColor}" stroke-width="2" opacity="0.5">
                      <animate attributeName="r" from="10" to="16" dur="0.5s" repeatCount="3"/>
                      <animate attributeName="opacity" from="0.8" to="0" dur="0.5s" repeatCount="3"/>
                    </circle>
                  ` : ''}
                  <circle cx="13" cy="13" r="10" fill="${markColor}" stroke="white" stroke-width="2.5"/>
                  ${delivery.flagged
                    ? '<text x="13" y="17" font-size="12" fill="white" text-anchor="middle" font-weight="bold">!</text>'
                    : `<path d="M8 12l5-4 5 4v5a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1z" fill="white"/>
                  <text x="13" y="20" font-size="6" fill="white" text-anchor="middle" font-weight="bold">${index + 1}</text>`}
                </svg>
              `)}`,
              scaledSize: { width: 26, height: 26 },
              anchor: { x: 13, y: 13 }
            }}
            zIndex={delivery.flagged ? 95 : 80}
            title={`Manual delivery #${index + 1} - ${new Date(delivery.timestamp).toLocaleTimeString()}${delivery.flagged ? ` — FLAGGED: marked ${delivery.nearest_house_m != null ? `${delivery.nearest_house_m}m` : 'far'} from nearest house` : ''}`}
          />
        );
      })}

      {/* Nearby house highlight circle */}
      {nearbyHouse && currentPosition && (
        <Circle
          center={{ lat: nearbyHouse.lat, lng: nearbyHouse.lon }}
          radius={proximityRadius}
          options={{
            fillColor: '#fbbf24',
            fillOpacity: 0.3,
            strokeColor: '#fbbf24',
            strokeWeight: 2
          }}
        />
      )}

      {/* Route path */}
      {routePolylinePath && (
        <Polyline
          path={routePolylinePath}
          options={{
            strokeColor: '#22c55e',
            strokeWeight: 5,
            strokeOpacity: 0.9
          }}
        />
      )}

      {/* Multi-contributor GPS trails (Admin Mark) */}
      {routeTrails.map((trail, i) => (
        (trail.points?.length > 1) && (
          <Polyline
            key={trail.id || i}
            path={trail.points.map(p => (Array.isArray(p) ? { lat: p[0], lng: p[1] } : p))}
            options={{
              strokeColor: trail.color || '#2563eb',
              strokeWeight: 3,
              strokeOpacity: 0.7,
              clickable: false,
              zIndex: 40,
            }}
          />
        )
      ))}

      {/* Current position marker */}
      {currentPosition && (
        <Marker
          position={{ lat: currentPosition[0], lng: currentPosition[1] }}
          icon={{
            url: `data:image/svg+xml,${encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill="url(#grad)" stroke="white" stroke-width="3"/>
                <defs>
                  <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#f97316"/>
                    <stop offset="100%" style="stop-color:#dc2626"/>
                  </linearGradient>
                </defs>
              </svg>
            `)}`,
            scaledSize: { width: 24, height: 24 },
            anchor: { x: 12, y: 12 }
          }}
          zIndex={100}
        />
      )}
    </GoogleMap>
  );
};

export default memo(TrackPageGoogleMap);
